import { FC, useState, useEffect } from 'react'
import {
  Card,
  CardBody,
  CardHeader,
  CardSubtitle,
  CardTitle,
  GridLayout,
  GridLayoutItem
} from '@progress/kendo-react-layout'
import { Window } from '@progress/kendo-react-dialogs'

import {
  BuildingInformationResObj,
  GetBuildingItemReqParams
} from '@gateway.Supplier'
import { BuildingItemGateway } from '../../gateway/supplier/BuildingItemGateway'
import { CommonActions, useAppDispatch } from '../../stores'

import Constants from '../../data/Constants.json'
import { ErrorMessage, ErrorMessages } from './ErrorMessages'
import { Tooltip } from '@progress/kendo-react-tooltip'

interface BuildingInformationProps {
  onClose: () => void
  buildingInfoParams: GetBuildingItemReqParams
}

export const BuildingInformation: FC<BuildingInformationProps> = (props) => {
  const dispatch = useAppDispatch()

  const [buildingInfo, setBuildingInfo] = useState<BuildingInformationResObj>()

  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])

  const convertFlagValue = (category: string, val?: number | null) => {
    if (category === undefined || val === undefined || val === null) {
      return '\xa0'
    }
    if (category === 'piling' || category === 'seismicIsolation') {
      return Constants.presenceFlag.find((obj) => obj.code === String(val))
        ?.name
    }
    if (category === 'topDown') {
      return Constants.topdownFlag.find((obj) => obj.code === String(val))?.name
    }
    return '\xa0'
  }

  useEffect(() => {
    dispatch(CommonActions.setIsLoading(true))
    BuildingItemGateway.getBuildingInformationResObj(props.buildingInfoParams)
      .then((res) => {
        if (!res.error) {
          setBuildingInfo(res.data)
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }, [props.buildingInfoParams])

  return (
    <Window
      title={'物件詳細'}
      onClose={props.onClose}
      initialWidth={1200}
      initialHeight={230}
      modal
    >
      <Tooltip position='right' style={{ zIndex: 100000 }}>
        <Card orientation="horizontal">
          <CardHeader>
            <CardTitle>
              <span
                style={{ fontSize: '1.2em', fontWeight: 'bold', color: 'gray' }}
              >
                {buildingInfo?.branchNm}
              </span>
            </CardTitle>
            <CardSubtitle>
              <span
                style={{
                  fontSize: '1.2em',
                  fontWeight: 'bold',
                  color: 'gray'
                }}
              >
                {buildingInfo?.procurementNo}-{buildingInfo?.subNo}-{buildingInfo?.expenseSeq}
              </span>
            </CardSubtitle>
            <CardSubtitle>
              <span
                style={{ fontSize: '1.2em', fontWeight: 'bold', color: 'gray' }}
              >
                {buildingInfo?.procurementSts}
              </span>
            </CardSubtitle>
          </CardHeader>
          <CardBody>
            <GridLayout gap={{ rows: 5, cols: 0 }}>
              <GridLayoutItem
                col={1}
                colSpan={12}
                row={1}
                style={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden'
                }}
              >
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: '1.2em'
                  }}
                  title={buildingInfo?.constructionNm ?? ''}
                >
                  {buildingInfo?.constructionNm}
                </span>
              </GridLayoutItem>
              <GridLayoutItem
                col={1}
                row={2}
                colSpan={3}
                style={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden'
                }}
              >
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  {buildingInfo?.architectureUse}
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  （
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  {buildingInfo?.structureUnderground}
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  /
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  {buildingInfo?.structureGround}
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  ）
                </span>
                <span
                  style={{
                    fontWeight: 'normal',
                    fontSize: '1.0em',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden'
                  }}
                  title={buildingInfo?.totalFloorAreaM2?.toLocaleString() ?? ''}
                >
                  {buildingInfo?.totalFloorAreaM2?.toLocaleString()}
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  ㎡/
                </span>
                <span
                  style={{
                    fontWeight: 'normal',
                    fontSize: '1.0em',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden'
                  }}
                  title={buildingInfo?.totalFloorAreaM2?.toLocaleString() ?? ''}
                >
                  {buildingInfo?.totalFloorAreaTsubo?.toLocaleString()}
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  坪 -（
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  {buildingInfo?.floorUnderGround}
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  /
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  {buildingInfo?.floorGround}
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                   /
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                   {buildingInfo?.floorPenthouse}
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  ）
                </span>
              </GridLayoutItem>
              <GridLayoutItem
                col={1}
                row={3}
                colSpan={3}
                style={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden'
                }}
              >
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  杭打:
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  {convertFlagValue('piling', buildingInfo?.piling)}
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  免震:
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  {convertFlagValue('seismicIsolation', buildingInfo?.seismicIsolation)}{' '}
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  {convertFlagValue('topDown', buildingInfo?.topDown)}
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  工法:
                </span>
                <span
                  style={{
                    fontWeight: 'normal',
                    fontSize: '1.0em',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden'
                  }}
                  title={buildingInfo?.specialConstructionMethod ?? ''}
                >
                  {buildingInfo?.specialConstructionMethod}
                </span>
              </GridLayoutItem>
              <GridLayoutItem col={1} row={4} colSpan={3}>
                <span>
                  {' '}
                  {buildingInfo?.startYM}-{buildingInfo?.completionYM}{' '}
                  {buildingInfo?.schedule}ヶ月
                </span>
                <span style={{ marginLeft: '5px' }}></span>
                <span
                  style={{
                    fontWeight: 'normal',
                    fontSize: '1.0em',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden'
                  }}
                  title={buildingInfo?.siteAreaM2?.toLocaleString() ?? ''}
                >
                  {buildingInfo?.siteAreaM2?.toLocaleString()}
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  ㎡/
                </span>
                <span
                  style={{
                    fontWeight: 'normal',
                    fontSize: '1.0em',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden'
                  }}
                  title={buildingInfo?.siteAreaM2?.toLocaleString() ?? ''}
                >
                  {buildingInfo?.siteAreaTsubo?.toLocaleString()}
                </span>
                <span style={{ fontWeight: 'normal', fontSize: '1.0em' }}>
                  坪
                </span>
              </GridLayoutItem>

              <GridLayoutItem col={4} row={2}>
                <span
                  style={{
                    fontWeight: 'normal',
                    fontSize: '1.0em',
                    verticalAlign: 'top'
                  }}
                >
                  工事部情報
                </span>
              </GridLayoutItem>
              <GridLayoutItem
                col={5}
                row={2}
                style={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden'
                }}
              >
                <span
                  style={{
                    fontSize: '1.0em',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    verticalAlign: 'top'
                  }}
                  title={buildingInfo?.constructionSectionNm ?? ''}
                >
                  {buildingInfo?.constructionSectionNm}
                </span>
                <span
                  style={{
                    fontSize: '1.0em',
                    verticalAlign: 'top'
                  }}
                >
                ）
                </span>
                <span
                  style={{
                    fontSize: '1.0em',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    verticalAlign: 'top'
                  }}
                  title={buildingInfo?.chiefPersonNm ?? ''}
                >
                  {buildingInfo?.chiefPersonNm}
                </span>
              </GridLayoutItem>
              <GridLayoutItem col={4} row={3}>
                <span
                  style={{
                    fontSize: '1.0em',
                    verticalAlign: 'top'
                  }}
                >
                  営業部情報（本社）
                </span>
              </GridLayoutItem>
              <GridLayoutItem
                col={5}
                row={3}
                style={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden'
                }}
              >
                <span
                  style={{
                    fontSize: '1.0em',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    verticalAlign: 'top'
                  }}
                  title={buildingInfo?.hqSalesSectionNm ?? ''}
                >
                  {buildingInfo?.hqSalesSectionNm}
                </span>
                <span
                  style={{
                    fontSize: '1.0em',
                    verticalAlign: 'top'
                  }}
                >
                  ）
                </span>
                <span
                  style={{
                    fontSize: '1.0em',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    verticalAlign: 'top'
                  }}
                  title={buildingInfo?.hqSalesPersonNm ?? ''}
                >
                  {buildingInfo?.hqSalesPersonNm}
                </span>
              </GridLayoutItem>
              <GridLayoutItem col={4} row={4}>
                <span
                  style={{
                    fontSize: '1.0em',
                    verticalAlign: 'top'
                  }}
                >
                  営業部情報（支店）
                </span>
              </GridLayoutItem>
              <GridLayoutItem
                col={5}
                row={4}
                style={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden'
                }}
              >
                <span
                  style={{
                    fontSize: '1.0em',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    verticalAlign: 'top'
                  }}
                  title={buildingInfo?.branchSalesSectionNm ?? ''}
                >
                  {buildingInfo?.branchSalesSectionNm}
                </span>
                <span
                  style={{
                    fontSize: '1.0em',
                    verticalAlign: 'top'
                  }}
                >
                ）
                </span>
                <span
                  style={{
                    fontSize: '1.0em',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    verticalAlign: 'top'
                  }}
                  title={buildingInfo?.branchSalesPersonNm ?? ''}
                >
                  {buildingInfo?.branchSalesPersonNm}
                </span>
              </GridLayoutItem>
            </GridLayout>
          </CardBody>
        </Card>
      </Tooltip>
      <ErrorMessages type="dialog" messages={errorMessages} />
    </Window>
  )
}
